export const learningAudienceActorTypes = ['company_owner', 'company_staff', 'company_agent', 'company_client'];

/** Existing audience writer contract; structure targets must come from the current directory picker. */
export function learningAudiencePayload(input, { selected, structure, version }) {
  let target;
  if (input.audience_type === 'all_company') target = '*';
  else if (input.audience_type === 'actor_type' && learningAudienceActorTypes.includes(input.actor_type)) target = input.actor_type;
  else if (input.audience_type === 'subject' && selected.size === 1) target = [...selected.keys()][0];
  else if (input.audience_type === 'structure') target = structure?.target;
  if (!target) throw new Error(input.audience_type === 'structure' ? 'audience_choose_structure' : 'choose_people');
  return { audience_type: input.audience_type, target, effect: input.effect, reason: input.reason,
    starts_at: input.starts_at || null, ends_at: input.ends_at || null, version: Number(version) };
}

/** Bounded, debounced directory search. A filtered empty page can still have a next raw cursor. */
export function createLearningAudienceSearch({ request, coursePath, onState = () => {}, isCurrent = () => true, schedule = setTimeout, cancel = clearTimeout }) {
  let generation = 0; let timer = null; let stopped = false;
  let state = { search: '', items: [], chosen: null, loading: false, available: true, unavailableReason: null, error: null, nextCursor: null, hasMore: false };
  const active = ticket => !stopped && ticket === generation && isCurrent();
  const emit = () => { if (!stopped && isCurrent()) onState({ ...state, items: [...state.items] }); };
  const fetchPage = async (cursor, ticket) => {
    if (!active(ticket) || state.loading) return;
    state.loading = true; state.error = null; emit();
    try {
      const query = new URLSearchParams({ type: 'structure', search: state.search, limit: '25' });
      if (cursor) query.set('cursor', String(cursor));
      const result = await request(`${coursePath}/audience-targets?${query}`);
      if (!active(ticket)) return;
      if (result.type !== 'structure' || typeof result.available !== 'boolean' || !Array.isArray(result.items) || result.items.length > 25) throw new Error('error');
      state.available = result.available; state.unavailableReason = result.unavailable_reason || null;
      if (!state.available) { state.items = []; state.chosen = null; state.nextCursor = null; state.hasMore = false; return; }
      const items = result.items.filter(row => row && typeof row.target === 'string' && /^ceo:company:[1-9][0-9]*:user:[1-9][0-9]*$/.test(row.target) && typeof row.name === 'string' && [0, 1, false, true].includes(row.active));
      const merged = new Map();
      items.forEach(row => merged.set(row.target, { target: row.target, name: row.name, active: Number(row.active) }));
      state.items = [...merged.values()];
      if (state.chosen && merged.has(state.chosen.target)) state.chosen = merged.get(state.chosen.target);
      const next = Number(result.next_cursor);
      state.nextCursor = Number.isSafeInteger(next) && next > cursor ? next : null;
      state.hasMore = Boolean(result.has_more && state.nextCursor);
    } catch (error) {
      if (active(ticket)) state.error = error.message || 'error';
    } finally {
      if (active(ticket)) { state.loading = false; emit(); }
    }
  };
  return {
    search(value, immediate = false) {
      cancel(timer); timer = null; generation++;
      state = { search: String(value).trim(), items: [], chosen: null, loading: false, available: true, unavailableReason: null, error: null, nextCursor: null, hasMore: false };
      emit();
      const size = [...state.search].length;
      if (size < 2 || size > 100 || stopped) return;
      const ticket = generation;
      if (immediate) return fetchPage(0, ticket);
      timer = schedule(() => { timer = null; fetchPage(0, ticket); }, 300);
    },
    next() { if (state.hasMore && !state.loading) return fetchPage(state.nextCursor, generation); },
    choose(target) {
      const row = state.items.find(item => item.target === target);
      if (stopped || !isCurrent() || state.loading || !state.available || !row) return false;
      state.chosen = row; emit(); return true;
    },
    selection() { return !stopped && isCurrent() && state.available && state.chosen ? { ...state.chosen } : null; },
    dispose() { stopped = true; generation++; cancel(timer); timer = null; state.chosen = null; },
  };
}
